import { FC, useState, useEffect } from 'react';

import ModalWrap from '../common/modal-wrap';
import { CloseCrossBtn } from '../icons/close-cross-btn';
import Button from '../common/button';

import { IClient, IConsultation } from '../../store/types';
import { getDate, getTime } from '../../utils/getTime';

type DeleteScope = 'one' | 'this_and_future' | 'series';

/** Встреча из повторяющейся серии — показываем выбор области удаления в календаре. */
function isRecurringConsultation(data: IConsultation): boolean {
  const ext = data as IConsultation & {
    is_recurring?: boolean;
    recurrence_series_id?: string | number | null;
  };
  if (ext.is_recurring === true) return true;
  if (ext.recurrence != null && ext.recurrence !== '') return true;
  if (ext.series_id != null && ext.series_id !== '') return true;
  if (ext.recurrence_series_id != null && ext.recurrence_series_id !== '') return true;
  return false;
}

interface IDeleteProps {
  open: boolean;
  close: () => void;
  deleteConsultation: (id: number, scope?: 'series' | 'this_and_future') => void;
  data: IConsultation | null;
  client?: Pick<IClient, 'id' | 'full_name' | 'time_zone'>;
  /** Удаление из календаря (для повторяющихся — отдельная модалка с выбором области) */
  isFromCalendar?: boolean;
}

export const DeleteConsultationForm: FC<IDeleteProps> = ({
  open,
  data,
  close,
  deleteConsultation,
  client,
  isFromCalendar = false,
}) => {
  const [deleteScope, setDeleteScope] = useState<DeleteScope>('one');

  const showRecurringCalendarDelete = isFromCalendar && data !== null && isRecurringConsultation(data);

  useEffect(() => {
    if (open && showRecurringCalendarDelete) setDeleteScope('one');
  }, [open, showRecurringCalendarDelete]);

  if (!data) return <></>;

  const handleDelete = () => {
    if (!data) return;
    if (showRecurringCalendarDelete && deleteScope === 'series') {
      deleteConsultation(data.id, 'series');
    } else if (showRecurringCalendarDelete && deleteScope === 'this_and_future') {
      deleteConsultation(data.id, 'this_and_future');
    } else {
      deleteConsultation(data.id);
    }
    close();
  };

  if (showRecurringCalendarDelete) {
    return (
      <ModalWrap isShow={open}>
        <div className="flex flex-col w-full max-w-[90%] sm:max-w-[480px] rounded-[20px] p-6 sm:p-8 bg-white shadow-lg">
          <div className="w-full flex justify-between items-start">
            <h2 className="font-comfortaa text-xl sm:text-2xl text-[#111026] font-bold leading-tight">
              Удаление повторяющегося
              <br />
              мероприятия
            </h2>
            <button type="button" className="cursor-pointer p-1" onClick={close} aria-label="Закрыть">
              <CloseCrossBtn width={20} height={20} />
            </button>
          </div>

          <div className="flex flex-col gap-4 mt-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="delete-scope"
                checked={deleteScope === 'one'}
                onChange={() => setDeleteScope('one')}
                className="w-4 h-4 text-redBase focus:ring-redBase"
              />
              <span className="font-manrope text-base text-[#2C3038]">Только это мероприятие</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="delete-scope"
                checked={deleteScope === 'this_and_future'}
                onChange={() => setDeleteScope('this_and_future')}
                className="w-4 h-4 text-redBase focus:ring-redBase"
              />
              <span className="font-manrope text-base text-[#2C3038]">Это и последующие мероприятия</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="delete-scope"
                checked={deleteScope === 'series'}
                onChange={() => setDeleteScope('series')}
                className="w-4 h-4 text-redBase focus:ring-redBase"
              />
              <span className="font-manrope text-base text-[#2C3038]">Все мероприятия</span>
            </label>
          </div>

          <div className="flex gap-4 justify-end mt-8">
            <Button type="grey" className="!min-w-0 !p-[10px_20px]" onClick={close}>
              Отмена
            </Button>
            <Button type="black" className="!min-w-0 !p-[10px_20px]" onClick={handleDelete}>
              Удалить
            </Button>
          </div>
        </div>
      </ModalWrap>
    );
  }

  return (
    <ModalWrap isShow={open}>
      <div className="think-scroll flex items-start gap-6 w-full max-w-[90%] sm:max-w-[60%] md:max-w-[560px] h-auto rounded-[20px] lg:rounded-[40px] p-5 lg:p-10 bg-white">
        <svg
          className="-mt-1.5 min-w-[40px] hidden md:block"
          width="40"
          height="41"
          viewBox="0 0 40 41"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20 3.23877C10.3359 3.23877 2.5 11.0747 2.5 20.7388C2.5 30.4028 10.3359 38.2388 20 38.2388C29.6641 38.2388 37.5 30.4028 37.5 20.7388C37.5 11.0747 29.6641 3.23877 20 3.23877ZM18.75 12.3013C18.75 12.1294 18.8906 11.9888 19.0625 11.9888H20.9375C21.1094 11.9888 21.25 12.1294 21.25 12.3013V22.9263C21.25 23.0981 21.1094 23.2388 20.9375 23.2388H19.0625C18.8906 23.2388 18.75 23.0981 18.75 22.9263V12.3013ZM20 29.4888C19.5093 29.4788 19.0422 29.2768 18.6987 28.9263C18.3553 28.5757 18.1629 28.1045 18.1629 27.6138C18.1629 27.123 18.3553 26.6518 18.6987 26.3013C19.0422 25.9507 19.5093 25.7488 20 25.7388C20.4907 25.7488 20.9578 25.9507 21.3013 26.3013C21.6447 26.6518 21.8371 27.123 21.8371 27.6138C21.8371 28.1045 21.6447 28.5757 21.3013 28.9263C20.9578 29.2768 20.4907 29.4788 20 29.4888Z"
            fill="#E0E0E8"
          />
        </svg>

        <div className="w-full flex flex-col gap-3.5 lg:gap-4">
          <div className="w-full flex justify-between items-center">
            <svg
              className="-mt-1.5 min-w-[35px] block md:hidden"
              width="40"
              height="41"
              viewBox="0 0 40 41"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 3.23877C10.3359 3.23877 2.5 11.0747 2.5 20.7388C2.5 30.4028 10.3359 38.2388 20 38.2388C29.6641 38.2388 37.5 30.4028 37.5 20.7388C37.5 11.0747 29.6641 3.23877 20 3.23877ZM18.75 12.3013C18.75 12.1294 18.8906 11.9888 19.0625 11.9888H20.9375C21.1094 11.9888 21.25 12.1294 21.25 12.3013V22.9263C21.25 23.0981 21.1094 23.2388 20.9375 23.2388H19.0625C18.8906 23.2388 18.75 23.0981 18.75 22.9263V12.3013ZM20 29.4888C19.5093 29.4788 19.0422 29.2768 18.6987 28.9263C18.3553 28.5757 18.1629 28.1045 18.1629 27.6138C18.1629 27.123 18.3553 26.6518 18.6987 26.3013C19.0422 25.9507 19.5093 25.7488 20 25.7388C20.4907 25.7488 20.9578 25.9507 21.3013 26.3013C21.6447 26.6518 21.8371 27.123 21.8371 27.6138C21.8371 28.1045 21.6447 28.5757 21.3013 28.9263C20.9578 29.2768 20.4907 29.4788 20 29.4888Z"
                fill="#E0E0E8"
              />
            </svg>
            <h2 className="font-comfortaa lg:text-2xl text-[#111026] font-bold">Удаление консультации</h2>
            <button className="cursor-pointer" onClick={() => close()}>
              <CloseCrossBtn width={20} height={20} />
            </button>
          </div>
          <div className="flex flex-col gap-4">
            <p className="max-w-[331px] font-comfortaa text-sm md:text-base font-semibold">
              Вы действительно хотите удалить консультацию {`«${client?.full_name}»`} ?
            </p>
            <div className="flex flex-col items-start gap-[8px] text-sm md:text-base">
              <div className="flex gap-[15px]">
                <p className="text-[#878791]">Дата встречи:</p>
                <p className="text-blackBase">{getDate(new Date(data.date))}</p>
              </div>
              <div className="flex gap-[15px]">
                <p className="text-[#878791] min-w-max">Ваше время:</p>
                <p className="text-blackBase">{`${getTime(
                  new Date(data.datetime_start),
                )} - ${getTime(new Date(data.datetime_end))} (${data.client_timezone})`}</p>
              </div>
            </div>
          </div>
          <div className="flex gap-6 justify-end mt-6">
            <Button className=" max-w-[160px] !text-sm md:!text-base py-[6px] px-2.5" type="grey" onClick={close}>
              Отмена
            </Button>
            <Button
              className="max-w-[160px] !text-sm md:!text-base py-[6px] px-2.5"
              type="black"
              onClick={handleDelete}
            >
              Удалить
            </Button>
          </div>
        </div>
      </div>
    </ModalWrap>
  );
};
